import styled from "styled-components";
import RecordSelect from "../../components/fields/RecordSelect";
import {
  BookingModel,
  CalendarNoteModel,
  IBooking,
  ICalendarNote, ILodgifyBooking,
  IUnit,
  NotionDatabaseModel,
  UnitModel
} from "../../types/models";
import useQuery from "../../hooks/useQuery";
import { useEffect, useState } from "react";
import { ancanaApi } from "../../api/ancanaApi";
import moment from "moment";
import { useTheme } from "../../contexts/ThemeProvider";
import RecordDrawer from "../../components/RecordDrawer";
import { Button, Checkbox, DatePicker, Tooltip, Segmented, notification } from "antd";
import {
  RightOutlined,
  LeftOutlined,
  ApiOutlined,
  CalendarOutlined, TableOutlined
} from "@ant-design/icons";
import CustomActionButton from "../../components/CustomActionButton";
import BookingsTable from '../../components/scheduling/BookingsTable';
import BookingsCalendar from "../../components/scheduling/BookingsCalendar";

const YearPicker: any = DatePicker.YearPicker;

const Container = styled.div`
  overflow: scroll;
  height: calc(100vh - 40px);
  padding-bottom: 50px;

  .shine-pulse {
    animation: pulse-border 1.5s ease-in-out infinite;
  }

  @keyframes pulse-border {
    0% {
      box-shadow: 0 0 0 0 rgb(251, 184, 46),
      inset 0 0 0 1px rgba(239, 173, 95, 0.9);
    }
    100% {
      box-shadow: 0 0 0 5px rgba(225, 169, 54, 0.1),
      inset 0 0 0 1px rgba(253, 169, 58, 0.3);
    }
  }

`;

function UnitCalendar() {
  const { query, handleFilter } = useQuery();
  const unitId = query.get('unit');
  const [unit, setUnit] = useState<IUnit>();
  const [unitToEdit, setUnitToEdit] = useState<IUnit | null>(null);
  const [unitVisibleFields, setUnitVisibleFields] = useState<string[]>([]);
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const [notes, setNotes] = useState<ICalendarNote[]>([]);
  const [noteToEdit, setNoteToEdit] = useState<ICalendarNote | null>(null);
  const [bookingToEdit, setBookingToEdit] = useState<IBooking | null>(null);
  const [createBookingDate, setCreateBookingDate] = useState<string>("");
  const [createNoteDate, setCreateNoteDate] = useState<string>("");
  const [externalBookings, setExternalBookings] = useState<ILodgifyBooking[]>([]);
  const [year, setYear] = useState(moment().year());
  const { currentTheme } = useTheme();
  const [view, setView] = useState('calendar');

  const fetchBookings = async (unitId: string, year: number) => {
    const res = await ancanaApi.get(
      `/bookings/?unit=${unitId}&start_date_after=${year-1}-12-27&start_date_before=${year}-12-31&page_size=100&ordering=start_date&expand=unit_owner.owner`
    );
    setBookings(res.data.results);
    return res;
  }

  useEffect(() => {
    if (unitId) {
      ancanaApi.get(`/housing-units/${unitId}/?expand=unit_ownerships.owner`).then(res => {
        setUnit(res.data);
      });
    }
  }, [unitId]);

  useEffect(() => {
    if (unitId) {
      const selectedBooking = query.get("booking");
      const instructions = query.get("actionText");
      handleFilter("booking", null, true);
      handleFilter("actionText", null, true);
      fetchBookings(unitId, year).then(res => {
        if (selectedBooking) {
          const booking = res.data.results.find((b: any) => b.id === Number(selectedBooking));
          if (booking) {
            setBookingToEdit(booking);
            if (instructions) notification.info({ message: instructions, placement: 'top', duration: 10 });
          }
        }
      });
    }
  }, [unitId, year]);

  useEffect(() => {
    if (unitId && showNotes && notes.length === 0) {
      ancanaApi.get(`/calendar-notes/?unit=${unitId}&year=${year}&page_size=100`).then(res => {
        setNotes(res.data.results);
      });
    }
  }, [unitId, year, showNotes]);

  useEffect(() => {
    if (!!unit && unit.lodgify_property) {
      ancanaApi.get(`/lodgify-bookings/?property=${unit.lodgify_property}&page_size=100&expand=guest`).then(res => {
        setExternalBookings(res.data.results);
      });
    }
  }, [unit]);

  return (
    <Container className="anti-contento">
      <div
        className="disp-flex h-stack-strict gap-sm justify-between mb-lg"
        style={{
          position: 'sticky',
          top: -16,
          zIndex: 3,
          padding: '20px 32px 4px 32px',
          background: currentTheme === 'dark' ? 'rgb(30, 30, 30)' : 'rgb(240, 242, 245)'
        }}
      >
        <div style={{ flex: 1 }}>
          <RecordSelect
            recordModel={UnitModel}
            filter
            recordsetFilters={{ 'has_no_owner': 'false' }}
            autoSelectFirst
          />
        </div>

        <div className="disp-flex h-stack-strict gap-xs">
          <Button onClick={() => setYear(year - 1)}><LeftOutlined /></Button>
          <YearPicker allowClear={false} style={{ width: 85, fontWeight: 600 }} value={moment().set('year', year)} onChange={(date: any) => setYear(date.year())} />
          <Button onClick={() => setYear(year + 1)}><RightOutlined /></Button>
        </div>

        <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end', alignItems: 'center' }} className="gap-xs">
          <Checkbox onChange={e => {setShowNotes(e.target.checked); console.log(e.target.checked)}}>Show notes</Checkbox>
          <Segmented options={[{label: <CalendarOutlined />, value: 'calendar'}, {label: <TableOutlined />, value: 'table'}]} value={view} onChange={value => setView(value as string)} />
          {unit?.notion_database ? (
            !!NotionDatabaseModel.customActions && <CustomActionButton customAction={NotionDatabaseModel.customActions[1]} recordModel={NotionDatabaseModel} recordId={unit.notion_database} />
          ) : (
            <Button icon={<ApiOutlined />} onClick={() => {if(unit) { setUnitToEdit(unit); setUnitVisibleFields(['notion_database'])}}}>Link Notion</Button>
          )}
          {!unit?.lodgify_property && (
            <Button icon={<ApiOutlined />} onClick={() => {if(unit) { setUnitToEdit(unit); setUnitVisibleFields(['lodgify_property'])}}}>Link Lodgify</Button>
          )}
        </div>
      </div>

      {unitId && view === 'calendar' && (
        <BookingsCalendar
          year={year}
          bookings={bookings}
          notes={showNotes ? notes : []}
          externalBookings={externalBookings}
          setNoteToEdit={setNoteToEdit}
          setBookingToEdit={setBookingToEdit}
          setCreateBookingDate={setCreateBookingDate}
          setCreateNoteDate={setCreateNoteDate}
        />
      )}
      {unitId && unit && view === 'table' && (
        <div className="contento">
          <BookingsTable
            bookings={bookings}
            unit={unit}
            setBookingToEdit={setBookingToEdit}
          />
        </div>
      )}

      <RecordDrawer
        forceOpen={createBookingDate !== ""}
        recordModel={BookingModel}
        recordInstance={bookingToEdit}
        visibleFields={['unit', 'unit_owner', 'start_date', 'end_date', 'status', 'trade_notes']}
        onClose={() => setCreateBookingDate("")}
        allowDelete
        recordsetFilters={{
          unit: (unitId?.toString() as any),
          start_date: createBookingDate,
          end_date: moment(createBookingDate).add(6, 'days').format('YYYY-MM-DD'),
        }}
        afterChange={!!unitId ? (bookid: any) => { fetchBookings(unitId, year); setCreateBookingDate(""); } : undefined}
      />
      <RecordDrawer
        recordModel={UnitModel}
        visibleFields={unitVisibleFields}
        recordInstance={unitToEdit}
        onClose={() => setUnitToEdit(null)}
      />
      <RecordDrawer
        allowDelete
        recordModel={CalendarNoteModel}
        forceOpen={createNoteDate !== ""}
        customRecordName="Note"
        recordInstance={noteToEdit}
        onClose={() => { setNoteToEdit(null); setCreateNoteDate("") }}
        recordsetFilters={ !!unitId ? { unit: unitId.toString(), start_date: createNoteDate } : undefined}
        afterSave={r => {
          console.log('>>>>> r', r);
        }}
      />
    </Container>
)
}

export default UnitCalendar;
